import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    crudStatusEnum,
    getCrudStatusLabel,
    getPriceTypeLabel,
    localStorageKeys,
    permissionLevelEnum,
    systemConfigKeyEnum,
} from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateTruckMutation } from '../../../../hooks/mutations/useTruckMutation';
import { useTrucksCompaniesQuery } from '../../../../hooks/queries/useTrucksCompaniesQuery';
import { useTrucksQuery } from '../../../../hooks/queries/useTrucksQuery';
import { useTrucksTypesQuery } from '../../../../hooks/queries/useTrucksTypesQuery';
import { useUsersQuery } from '../../../../hooks/queries/useUsersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';
import { Truck } from '../../../../services/apiSLTP/materials/trucks';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { formatNumber } from '../../../../utils/numbers';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import { InheritedPriceDisplay } from '../../../domain/InheritedPriceDisplay';
import LicensePlate from '../../../domain/LicensePlate';
import UpdateTruckModal from '../../../modals/admin/vehicles/UpdateTruckModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface TrucksSettingsRowProps {
    withUsageCount: boolean;
}

const TrucksSettingsRow: React.FC<TrucksSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTruck, setSelectedTruck] = useState<Truck | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const {
        data: dataUsers,
        isLoading: usersIsLoading,
        isError: usersIsError,
        isSuccess: usersIsSuccess,
        refetch: usersRefetch,
    } = useUsersQuery({ queries: { includeFiles: true } });

    const {
        data: dataTruckTypes,
        isLoading: trucksTypesIsLoading,
        isError: trucksTypesIsError,
        isSuccess: trucksTypesIsSuccess,
        refetch: trucksTypesRefetch,
    } = useTrucksTypesQuery({});
    const {
        data: dataTruckCompanies,
        isLoading: trucksCompaniesIsLoading,
        isError: trucksCompaniesIsError,
        isSuccess: trucksCompaniesIsSuccess,
        refetch: trucksCompaniesRefetch,
    } = useTrucksCompaniesQuery({});

    const {
        data: dataTrucks,
        isLoading,
        isError,
        isSuccess,
        refetch,
    } = useTrucksQuery({
        queries: { withUsage: withUsageCount },
    });
    const {
        mutate: mutateTruck,
        isSuccess: isSuccessTruck,
        isError: isErrorTruck,
        isLoading: isLoadingTruck,
        error: errorTruck,
        data: dataTruck,
    } = useUpdateTruckMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating truck:', error.message);
        },
    });

    const sortedTrucks = () => {
        return (
            dataTrucks?.sort((a, b) => {
                const companyA = a?.truckCompanyId.isOwnCompany;
                const companyB = b?.truckCompanyId.isOwnCompany;
                if (companyA && !companyB) return -1;
                if (companyB && !companyA) return 1;
                const typeA = a?.truckTypeId.displayOrder || 0;
                const typeB = b?.truckTypeId.displayOrder || 0;
                if (typeA < typeB) return -1;
                if (typeA > typeB) return 1;
                return a.designation.localeCompare(b.designation);
            }) || []
        );
    };

    const columnDefs: ColDef<Truck>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
            initialWidth: 150,
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<Truck>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyTruck'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedTruck(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteTruck'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedTruck(params.data);
                                    setIsEditMode(false);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<Truck>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('plateNumber')),
            field: 'plateNumber',
            cellRenderer: (params: ICellRendererParams<Truck>) => {
                if (!params.data) return params.value;
                return <LicensePlate number={params.data.plateNumber} />;
            },
            colId: 'plateNumber',
        },
        {
            headerName: capitalizeFirstLetter(t('driver')),
            valueGetter: (params) => formatName(params.data?.userId?.firstName, params.data?.userId?.lastName),
            colId: 'driver',
        },
        {
            headerName: capitalizeFirstLetter(t('company')),
            field: 'truckCompanyId',
            valueGetter: (params) => dataTruckCompanies?.find((c) => c._id === params.data?.truckCompanyId?._id)?.designation,
            colId: 'company',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'truckTypeId',
            valueGetter: (params) => dataTruckTypes?.find((c) => c._id === params.data?.truckTypeId?._id)?.designation,
            colId: 'type',
        },
        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            valueGetter: (params) => params.data?.price || params.data?.truckTypeId?.price,
            cellRenderer: (params: ICellRendererParams<Truck>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.truckTypeId?.price}
                        format={(v) => formatNumber(v, t('locale'))}
                    />
                );
            },
            colId: 'price',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => capitalizeFirstLetter(t(getPriceTypeLabel(params.data?.priceType || params.data?.truckTypeId?.priceType))),
            cellRenderer: (params: ICellRendererParams<Truck>) => {
                if (!params.data) return params.value;
                return (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.truckTypeId?.priceType}
                        format={(v) => capitalizeFirstLetter(t(getPriceTypeLabel(v)))}
                    />
                );
            },
            colId: 'pricing',
        },
        createDateTimeColumn<Truck>('startDate', 'startDate'),
        createDateTimeColumn<Truck>('endDate', 'endDate'),
        {
            headerName: capitalizeFirstLetter(t('model')),
            field: 'model',
            colId: 'model',
        },
        {
            headerName: capitalizeFirstLetter(t('baseName')),
            field: 'baseName',
            colId: 'baseName',
        },
        {
            headerName: capitalizeFirstLetter(t('complete')),
            valueGetter: (params) => !params.data?.isIncomplete,
            colId: 'complete',
            cellRenderer: 'agCheckboxCellRenderer',
            valueFormatter: (params) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
            filterParams: {
                valueFormatter: (params: any) => (params.value ? capitalizeFirstLetter(t('true')) : capitalizeFirstLetter(t('false'))),
            },
        },
        createDateTimeColumn<Truck>('createdAt', 'createdAt'),
        createDateTimeColumn<Truck>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
                colId: '_id',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xtotal',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesPlanned')),
                field: 'usageCount.dailyTrucksPlanned',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xridesplanned',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xRidesCompleted')),
                field: 'usageCount.dailyTrucksRides',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                colId: 'xridescompleted',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    return (
        <>
            <GenericAgGridTable
                rowData={sortedTrucks().filter((item) => item.crudStatus !== crudStatusEnum.DELETED)}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-trucks"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedTruck(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addTruck'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.VEHICLES.TRUCKS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />
            {showEditModal && (
                <UpdateTruckModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    truck={selectedTruck}
                    isEditMode={isEditMode}
                    trucks={dataTrucks || []}
                    trucksTypes={dataTruckTypes || []}
                    trucksCompanies={dataTruckCompanies || []}
                    refetch={() => refetch()}
                    users={dataUsers || []}
                />
            )}
            {showDeleteModal && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        if (selectedTruck) {
                            mutateTruck({ truckId: selectedTruck._id, data: { crudStatus: crudStatusEnum.DELETED } });
                        }
                    }}
                    title={t('truck')}
                    itemName={selectedTruck?.designation}
                />
            )}
        </>
    );
};

export default TrucksSettingsRow;
